import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Heart, Sparkles, ArrowLeft, Save, Upload, X, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import heroImage from "@/assets/hero-image.jpg";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { AvatarCropDialog } from "@/components/AvatarCropDialog";
import { profileSchema } from "@/lib/validationSchemas";

const journeyStages = [
  { value: "exploring", label: "Just Exploring", description: "I'm starting to notice changes and want to learn more" },
  { value: "perimenopause", label: "Perimenopause", description: "I'm experiencing irregular periods and symptoms" },
  { value: "menopause", label: "Menopause", description: "It's been 12+ months since my last period" },
  { value: "postmenopause", label: "Postmenopause", description: "I'm navigating life after menopause" },
  { value: "unsure", label: "Not Sure", description: "I'm figuring it out as I go" }
];

const commonSymptoms = [
  "Hot Flashes",
  "Night Sweats",
  "Sleep Issues",
  "Mood Changes",
  "Brain Fog",
  "Anxiety",
  "Fatigue",
  "Weight Changes",
  "Joint Pain",
  "Headaches"
];

const commonGoals = [
  "Better Sleep",
  "Manage Hot Flashes",
  "Improve Mood",
  "Increase Energy",
  "Maintain Weight",
  "Reduce Anxiety",
  "Better Focus",
  "Stay Active",
  "Connect with Others",
  "Learn More"
];

export default function EditProfile() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [displayName, setDisplayName] = useState("");
  const [journeyStage, setJourneyStage] = useState("");
  const [selectedSymptoms, setSelectedSymptoms] = useState<string[]>([]);
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate("/auth");
        return;
      }

      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (error) throw error;

      if (data) {
        setDisplayName(data.display_name || "");
        setJourneyStage(data.journey_stage || "");
        setSelectedSymptoms(data.symptoms || []);
        setSelectedGoals(data.goals || []);
        setAvatarUrl(data.avatar_url || null);
      }
    } catch (error: any) {
      toast({
        title: "Error loading profile",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSymptomToggle = (symptom: string) => {
    setSelectedSymptoms(prev =>
      prev.includes(symptom)
        ? prev.filter(s => s !== symptom)
        : [...prev, symptom]
    );
  };

  const handleGoalToggle = (goal: string) => {
    setSelectedGoals(prev =>
      prev.includes(goal)
        ? prev.filter(g => g !== goal)
        : [...prev, goal]
    );
  };

  const handleAvatarSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Avatar must be less than 5MB",
        variant: "destructive"
      });
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file",
        variant: "destructive"
      });
      return;
    }

    // Create object URL for the selected image
    const imageSrc = URL.createObjectURL(file);
    setSelectedImageSrc(imageSrc);
    setCropDialogOpen(true);

    // Reset the input so the same file can be selected again
    e.target.value = '';
  };

  const handleCropComplete = async (croppedImage: Blob) => {
    setUploading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const fileName = `${user.id}/${Date.now()}.jpg`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, croppedImage, { 
          upsert: true,
          contentType: 'image/jpeg'
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      setAvatarUrl(publicUrl);

      toast({
        title: "Avatar uploaded",
        description: "Don't forget to save your changes!"
      });
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setUploading(false);
      if (selectedImageSrc) {
        URL.revokeObjectURL(selectedImageSrc);
        setSelectedImageSrc(null);
      }
    }
  };

  const handleRemoveAvatar = () => {
    setAvatarUrl(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all inputs
    const validation = profileSchema.safeParse({
      displayName,
      journeyStage,
      symptoms: selectedSymptoms,
      goals: selectedGoals,
      avatarUrl,
    });

    if (!validation.success) {
      const firstError = validation.error.errors[0];
      toast({
        title: "Validation Error",
        description: firstError.message,
        variant: "destructive"
      });
      return;
    }

    setSaving(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("No user found");
      }

      const { error } = await supabase
        .from("profiles")
        .update({
          display_name: displayName,
          journey_stage: journeyStage,
          symptoms: selectedSymptoms,
          goals: selectedGoals,
          avatar_url: avatarUrl,
          updated_at: new Date().toISOString()
        })
        .eq("user_id", user.id);

      if (error) throw error;

      toast({
        title: "Profile Updated",
        description: "Your changes have been saved successfully"
      });

      navigate("/profile");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/10">
        <p className="text-muted-foreground">Loading profile...</p>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen relative overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(135deg, rgba(145, 25, 55, 0.85), rgba(175, 30, 50, 0.75)), url(${heroImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-primary/30 to-secondary/30 rounded-full blur-3xl opacity-50" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-accent/30 to-primary/30 rounded-full blur-3xl opacity-50" />

      <div className="relative z-10 container mx-auto px-4 py-12">
        <Link to="/profile">
          <Button variant="ghost" className="mb-6 gap-2 text-white hover:bg-white/10">
            <ArrowLeft className="h-4 w-4" />
            Back to Profile
          </Button>
        </Link>

        <Card className="max-w-4xl mx-auto bg-background/95 backdrop-blur-xl shadow-2xl border-2 border-primary/20 animate-fade-in">
          <CardHeader className="text-center pb-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary mb-4 mx-auto shadow-lg">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-3xl font-bold">
              Edit Your Profile
            </CardTitle>
            <p className="text-muted-foreground mt-2">
              Update your information to personalize your experience
            </p>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Avatar Upload */}
              <div className="space-y-3">
                <Label className="text-lg font-semibold flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  Profile Avatar
                </Label>
                <div className="flex items-center gap-6">
                  <Avatar className="h-24 w-24 border-2 border-primary/20">
                    <AvatarImage src={avatarUrl || undefined} />
                    <AvatarFallback className="text-2xl bg-primary/10">
                      {displayName.charAt(0).toUpperCase() || "?"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="avatar-upload" className="cursor-pointer">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={uploading}
                        className="gap-2"
                        onClick={() => document.getElementById('avatar-upload')?.click()}
                      >
                        <Upload className="h-4 w-4" />
                        {uploading ? "Uploading..." : "Upload Avatar"}
                      </Button>
                    </Label>
                    <input
                      id="avatar-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarSelect}
                      className="hidden"
                    />
                    {avatarUrl && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleRemoveAvatar}
                        className="gap-2 w-fit"
                      >
                        <X className="h-4 w-4" />
                        Remove
                      </Button>
                    )}
                    <p className="text-xs text-muted-foreground">
                      JPG, PNG, GIF or WebP. Max 5MB.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Image will be cropped to 512x512 pixels.
                    </p>
                  </div>
                </div>
              </div>

              {/* Display Name */}
              <div className="space-y-3">
                <Label htmlFor="displayName" className="text-lg font-semibold flex items-center gap-2">
                  <Heart className="h-5 w-5 text-primary" />
                  Your Name
                </Label>
                <Input
                  id="displayName"
                  placeholder="What should we call you?"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="text-lg p-6"
                  required
                />
              </div>

              {/* Journey Stage */}
              <div className="space-y-3">
                <Label className="text-lg font-semibold">
                  Where Are You in Your Journey?
                </Label>
                <RadioGroup value={journeyStage} onValueChange={setJourneyStage} className="space-y-3">
                  {journeyStages.map((stage) => (
                    <label key={stage.value} htmlFor={stage.value}>
                      <Card
                        className={`cursor-pointer transition-all hover:shadow-md ${
                          journeyStage === stage.value ? 'border-2 border-primary bg-primary/5' : 'border'
                        }`}
                      >
                        <CardContent className="flex items-start gap-4 p-4">
                          <RadioGroupItem value={stage.value} id={stage.value} className="mt-1" />
                          <div className="flex-1">
                            <span className="text-base font-semibold cursor-pointer">
                              {stage.label}
                            </span>
                            <p className="text-sm text-muted-foreground mt-1">
                              {stage.description}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              {/* Symptoms */}
              <div className="space-y-3">
                <Label className="text-lg font-semibold">
                  Current Symptoms
                </Label>
                <p className="text-sm text-muted-foreground mb-4">
                  Select all that you're currently experiencing:
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {commonSymptoms.map((symptom) => (
                    <label key={symptom} className="cursor-pointer">
                      <Card
                        className={`transition-all hover:shadow-md ${
                          selectedSymptoms.includes(symptom) ? 'border-2 border-primary bg-primary/5' : 'border'
                        }`}
                      >
                        <CardContent className="flex items-center gap-3 p-4">
                          <Checkbox
                            checked={selectedSymptoms.includes(symptom)}
                            onCheckedChange={() => handleSymptomToggle(symptom)}
                          />
                          <span className="text-sm font-medium">{symptom}</span>
                        </CardContent>
                      </Card>
                    </label>
                  ))}
                </div>
              </div>

              {/* Goals */}
              <div className="space-y-3">
                <Label className="text-lg font-semibold">
                  Your Wellness Goals
                </Label>
                <p className="text-sm text-muted-foreground mb-4">
                  What would you like to focus on?
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {commonGoals.map((goal) => (
                    <label key={goal} className="cursor-pointer">
                      <Card
                        className={`transition-all hover:shadow-md ${
                          selectedGoals.includes(goal) ? 'border-2 border-primary bg-primary/5' : 'border'
                        }`}
                      >
                        <CardContent className="flex items-center gap-3 p-4">
                          <Checkbox
                            checked={selectedGoals.includes(goal)}
                            onCheckedChange={() => handleGoalToggle(goal)}
                          />
                          <span className="text-sm font-medium">{goal}</span>
                        </CardContent>
                      </Card>
                    </label>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex gap-4 pt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/profile")}
                  className="flex-1"
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 gap-2"
                  disabled={saving || uploading}
                >
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {saving ? "Saving..." : "Save Changes"}
                  {!saving && <Save className="h-4 w-4" />}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {selectedImageSrc && (
          <AvatarCropDialog
            open={cropDialogOpen}
            onOpenChange={(open) => {
              setCropDialogOpen(open);
              if (!open && selectedImageSrc) {
                URL.revokeObjectURL(selectedImageSrc);
                setSelectedImageSrc(null);
              }
            }}
            imageSrc={selectedImageSrc}
            onCropComplete={handleCropComplete}
          />
        )}
      </div>
    </div>
  );
}
